import { UserRole } from 'shared/types/userRole';
import { BasePermissionManager } from './basePermissionsManager';
import { PermissionTranslationKeys } from './translationKeys';
import { Permission, PermissionConfig } from './types';
type BusinessRuleValidator<T> = (params: T) => Permission;

interface PermissionRules<TCreate, TEdit, TDelete> {
  businessRulesForCreate?: BusinessRuleValidator<TCreate>[];
  businessRulesForEdit?: BusinessRuleValidator<TEdit>[];
  businessRulesForDelete?: BusinessRuleValidator<TDelete>[];
}
interface PermissionManagerConfig<
  TCreate = void,
  TEdit = void,
  TDelete = void,
> extends PermissionRules<TCreate, TEdit, TDelete> {
  permissionConfigForCreate: PermissionConfig;
  permissionConfigForEdit: PermissionConfig;
  permissionConfigForDelete: PermissionConfig | null;
}

type PermissionManagerClass<TCreate, TEdit, TDelete> = {
  new (userRole: UserRole): {
    canCreate(isOffline: boolean, ...params: TCreate extends void ? [] : [TCreate]): Permission;
    canEdit(isOffline: boolean, ...params: TEdit extends void ? [] : [TEdit]): Permission;
    canDelete(isOffline: boolean, ...params: TDelete extends void ? [] : [TDelete]): Permission;
  };
};

export class PermissionManagerFactory {
  static create<TCreate = void, TEdit = void, TDelete = void>({
    permissionConfigForCreate,
    permissionConfigForEdit,
    permissionConfigForDelete,
    businessRulesForCreate = [],
    businessRulesForEdit = [],
    businessRulesForDelete = [],
  }: PermissionManagerConfig<
    TCreate,
    TEdit,
    TDelete
  >): PermissionManagerClass<TCreate, TEdit, TDelete> {
    return class extends BasePermissionManager {
      constructor(public readonly userRole: UserRole) {
        super();
      }

      canCreate(isOffline: boolean, params?: TCreate): Permission {
        const basePermission = this.getBasePermission(
          isOffline,
          permissionConfigForCreate
        );

        return this.validateBusinessRules(
          basePermission,
          businessRulesForCreate,
          params
        );
      }

      canEdit(isOffline: boolean, params?: TEdit): Permission {
        const basePermission = this.getBasePermission(
          isOffline,
          permissionConfigForEdit
        );

        return this.validateBusinessRules(
          basePermission,
          businessRulesForEdit,
          params
        );
      }

      canDelete(isOffline: boolean, params?: TDelete): Permission {
        if (!permissionConfigForDelete) {
          throw new Error('Permission config for delete is not defined');
        }

        const basePermission = this.getBasePermission(
          isOffline,
          permissionConfigForDelete
        );

        return this.validateBusinessRules(
          basePermission,
          businessRulesForDelete,
          params
        );
      }

      private getBasePermission(
        isOffline: boolean,
        config: PermissionConfig
      ): Permission {
        if (isOffline) {
          return this.noPermissionInOffline();
        }

        return this.createPermission(
          config[this.userRole],
          PermissionTranslationKeys.ACTION_FORBIDDEN
        );
      }

      private validateBusinessRules<T>(
        basePermission: Permission,
        rules: BusinessRuleValidator<T>[],
        params?: T
      ): Permission {
        if (!basePermission.permission) {
          return basePermission;
        }

        for (const rule of rules) {
          const ruleResult = rule(params as T);
          if (!ruleResult.permission) {
            return ruleResult;
          }
        }

        return basePermission;
      }
    };
  }
}
